import { useState } from 'react';

interface VideoManagerProps {
  vimeoId: string;
  onChange: (id: string) => void;
}

function extractVimeoId(input: string): string {
  const trimmed = input.trim();
  // If it's already just a numeric ID
  if (/^\d+$/.test(trimmed)) return trimmed;
  // Extract from URL: vimeo.com/123456789
  const match = trimmed.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : trimmed;
}

export default function VideoManager({ vimeoId, onChange }: VideoManagerProps) {
  const [inputVal, setInputVal] = useState(vimeoId);
  const [previewing, setPreviewing] = useState(!!vimeoId);

  const handleChange = (val: string) => {
    setInputVal(val);
    const extracted = extractVimeoId(val);
    onChange(extracted);
    setPreviewing(false);
  };

  const currentId = extractVimeoId(inputVal);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">Video del módulo</p>
        <p className="text-[#F8F6F3]/40 text-sm">Pega la URL de Vimeo o el ID numérico del video</p>
      </div>

      {/* Input */}
      <div className="flex flex-col gap-2">
        <label className="text-[#F8F6F3]/50 text-xs uppercase tracking-[2px]">URL o ID de Vimeo</label>
        <div className="flex gap-3">
          <input
            type="text"
            value={inputVal}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="https://vimeo.com/123456789 o simplemente 123456789"
            className="flex-1 px-4 py-3 rounded-xl text-sm text-[#F8F6F3] placeholder-[#F8F6F3]/20 outline-none"
            style={{ background: 'rgba(248,246,243,0.06)', border: '1px solid rgba(248,246,243,0.10)' }}
          />
          <button
            onClick={() => setPreviewing(true)}
            disabled={!currentId}
            className="px-5 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-30"
            style={{ background: 'rgba(139,157,131,0.20)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.30)' }}
          >
            <i className="ri-eye-line mr-2" />
            Previsualizar
          </button>
        </div>
        {currentId && currentId !== inputVal && (
          <p className="text-[#8B9D83]/60 text-[10px]">ID detectado: {currentId}</p>
        )}
      </div>

      {/* Preview */}
      {previewing && currentId && (
        <div className="flex flex-col gap-3">
          <p className="text-[#F8F6F3]/40 text-xs uppercase tracking-[2px]">Vista previa</p>
          <div
            className="relative rounded-2xl overflow-hidden w-full"
            style={{ aspectRatio: '16/9', background: '#0D0F12' }}
          >
            <iframe
              src={`https://player.vimeo.com/video/${currentId}?badge=0&autopause=0&color=8B9D83&title=0&byline=0&portrait=0`}
              className="absolute inset-0 w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Vimeo preview"
              style={{ border: 'none' }}
            />
          </div>
        </div>
      )}

      {/* Empty state */}
      {!currentId && (
        <div
          className="flex flex-col items-center justify-center py-14 rounded-2xl gap-3"
          style={{ background: 'rgba(248,246,243,0.02)', border: '1px dashed rgba(248,246,243,0.10)' }}
        >
          <div className="w-12 h-12 flex items-center justify-center">
            <i className="ri-video-line text-[#F8F6F3]/20 text-3xl" />
          </div>
          <p className="text-[#F8F6F3]/30 text-sm">Sin video asignado</p>
        </div>
      )}
    </div>
  );
}
